import { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { AlertCircle, Loader2, Plus, Trash2 } from 'lucide-react';
import { apiClient } from '../../../lib/api';
import type { Collateral, Company, Facility } from './types';
import {
  COLLATERAL_STATUSES,
  COLLATERAL_TYPES,
  FINANCING_SOURCES,
  INSTRUMENT_TYPES,
  LOAN_TYPES,
  fmtMoney,
  fmtRate,
  isLoanInstrument,
  labelOf,
} from './types';

/**
 * Credit Portfolio tab — combined view of credit_facilities and collaterals
 * inside the Case Detail screen.
 *
 * This is the **quick-entry** surface during case review: facilities and
 * collaterals stacked in one tab, both filtered by company. The forms expose
 * the high-leverage fields used in most worksheets.
 *
 * For deep editing of edge fields (balloon, prepayment penalty, multiple
 * value snapshots, full haircut workflow, etc.), the user navigates to the
 * standalone editor pages:
 *   • /business/cases/:caseId/facilities  → CreditFacilitiesPage
 *   • /business/cases/:caseId/collaterals → CollateralsPage
 *
 * All three views read the same backend (`/facilities`, `/collaterals`) and
 * share constants/types from `./types.ts` — keep that file as the single
 * source of truth for instrument/collateral taxonomies.
 */

export function CreditPortfolioTab({ caseId }: { caseId: string }) {
  const companiesQuery = useQuery<Company[]>({
    queryKey: ['companies', caseId],
    queryFn: () => apiClient.get<Company[]>(`/companies?case_id=${caseId}`),
  });

  const companies = companiesQuery.data ?? [];

  const facilitiesQuery = useQuery<Facility[]>({
    queryKey: ['facilities', 'case', caseId],
    queryFn: () => apiClient.get<Facility[]>(`/facilities?case_id=${caseId}`),
  });

  const collateralsQuery = useQuery<Collateral[]>({
    queryKey: ['collaterals', 'case', caseId],
    queryFn: () => apiClient.get<Collateral[]>(`/collaterals?case_id=${caseId}`),
  });

  const companyMap = useMemo(() => {
    const m = new Map<string, Company>();
    for (const c of companies) m.set(c.id, c);
    return m;
  }, [companies]);

  if (companies.length === 0 && companiesQuery.isFetched) {
    return (
      <div className="card text-center text-sm text-slate-600 py-8">
        עוד לא הוזנו חברות בקייס. עבור לטאב "חברות" והוסף לפחות חברה אחת.
      </div>
    );
  }

  return (
    <div className="space-y-8">
      <FacilitiesSection
        caseId={caseId}
        companies={companies}
        companyMap={companyMap}
        facilities={facilitiesQuery.data ?? []}
        loading={facilitiesQuery.isLoading}
        error={facilitiesQuery.error as Error | null}
      />

      <CollateralsSection
        caseId={caseId}
        companies={companies}
        companyMap={companyMap}
        collaterals={collateralsQuery.data ?? []}
        facilities={facilitiesQuery.data ?? []}
        loading={collateralsQuery.isLoading}
        error={collateralsQuery.error as Error | null}
      />
    </div>
  );
}

// ---------------------------------------------------------------------------
// Facilities
// ---------------------------------------------------------------------------

type FacForm = {
  company_id: string;
  instrument_type: string;
  loan_type: string;
  financing_source_type: string;
  bank_name: string;
  facility_label: string;
  amount_current: string;
  annual_rate_percent: string;
  term_months: string;
  current_annual_maturities: string;
  notes: string;
};

const facEmpty: FacForm = {
  company_id: '',
  instrument_type: 'long_term_loan',
  loan_type: 'SHPITZER',
  financing_source_type: 'commercial_bank',
  bank_name: '',
  facility_label: '',
  amount_current: '',
  annual_rate_percent: '',
  term_months: '',
  current_annual_maturities: '',
  notes: '',
};

function FacilitiesSection({
  caseId,
  companies,
  companyMap,
  facilities,
  loading,
  error,
}: {
  caseId: string;
  companies: Company[];
  companyMap: Map<string, Company>;
  facilities: Facility[];
  loading: boolean;
  error: Error | null;
}) {
  const qc = useQueryClient();
  const [adding, setAdding] = useState(false);
  const [form, setForm] = useState<FacForm>(facEmpty);

  const create = useMutation<Facility, Error, FacForm>({
    mutationFn: (s) => {
      const body: Record<string, unknown> = {
        company_id: s.company_id,
        instrument_type: s.instrument_type,
        financing_source_type: s.financing_source_type,
      };
      if (isLoanInstrument(s.instrument_type) && s.loan_type) body.loan_type = s.loan_type;
      if (s.bank_name) body.bank_name = s.bank_name;
      if (s.facility_label) body.facility_label = s.facility_label;
      if (s.amount_current) body.amount_current = s.amount_current;
      if (s.annual_rate_percent) {
        body.annual_rate = (Number(s.annual_rate_percent) / 100).toString();
      }
      if (s.term_months) body.term_months = Number(s.term_months);
      if (s.current_annual_maturities) {
        body.current_annual_maturities = s.current_annual_maturities;
      }
      if (s.notes) body.notes = s.notes;
      return apiClient.post<Facility>('/facilities', body);
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['facilities', 'case', caseId] });
      setForm(facEmpty);
      setAdding(false);
    },
  });

  const remove = useMutation<void, Error, string>({
    mutationFn: (id) => apiClient.delete(`/facilities/${id}`),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['facilities', 'case', caseId] });
    },
  });

  const startAdd = () => {
    const primary = companies.find((c) => c.is_primary) ?? companies[0];
    setForm({ ...facEmpty, company_id: primary?.id ?? '' });
    setAdding(true);
  };

  return (
    <section>
      <div className="flex items-center justify-between mb-3">
        <h3 className="text-lg font-semibold text-navy">מסגרות אשראי והלוואות</h3>
        {!adding && (
          <button
            type="button"
            onClick={startAdd}
            className="btn-primary inline-flex items-center gap-2 text-sm"
          >
            <Plus className="w-4 h-4" />
            הוסף הלוואה
          </button>
        )}
      </div>

      {adding && (
        <div className="card mb-4">
          <h4 className="font-semibold text-navy mb-4">הלוואה חדשה</h4>
          <div className="grid md:grid-cols-3 gap-4">
            <Field label="חברה">
              <select
                value={form.company_id}
                onChange={(e) => setForm({ ...form, company_id: e.target.value })}
                className="input"
              >
                <option value="">— בחר חברה —</option>
                {companies.map((c) => (
                  <option key={c.id} value={c.id}>
                    {c.short_label}
                    {c.is_primary ? ' ★' : ''}
                  </option>
                ))}
              </select>
            </Field>
            <Field label="סוג מכשיר">
              <select
                value={form.instrument_type}
                onChange={(e) => setForm({ ...form, instrument_type: e.target.value })}
                className="input"
              >
                {INSTRUMENT_TYPES.map((o) => (
                  <option key={o.value} value={o.value}>
                    {o.label}
                  </option>
                ))}
              </select>
            </Field>
            <Field label="מקור מימון">
              <select
                value={form.financing_source_type}
                onChange={(e) => setForm({ ...form, financing_source_type: e.target.value })}
                className="input"
              >
                {FINANCING_SOURCES.map((o) => (
                  <option key={o.value} value={o.value}>
                    {o.label}
                  </option>
                ))}
              </select>
            </Field>
            <Field label="שם בנק / מקור">
              <input
                type="text"
                value={form.bank_name}
                onChange={(e) => setForm({ ...form, bank_name: e.target.value })}
                className="input"
              />
            </Field>
            <Field label="תווית">
              <input
                type="text"
                value={form.facility_label}
                onChange={(e) => setForm({ ...form, facility_label: e.target.value })}
                className="input"
              />
            </Field>
            <Field label="יתרה קיימת (₪)">
              <input
                type="number"
                value={form.amount_current}
                onChange={(e) => setForm({ ...form, amount_current: e.target.value })}
                className="input num"
              />
            </Field>
            {isLoanInstrument(form.instrument_type) && (
              <Field label="סוג הלוואה">
                <select
                  value={form.loan_type}
                  onChange={(e) => setForm({ ...form, loan_type: e.target.value })}
                  className="input"
                >
                  {LOAN_TYPES.map((o) => (
                    <option key={o.value} value={o.value}>
                      {o.label}
                    </option>
                  ))}
                </select>
              </Field>
            )}
            <Field label="ריבית שנתית (%)">
              <input
                type="number"
                step="0.01"
                value={form.annual_rate_percent}
                onChange={(e) =>
                  setForm({ ...form, annual_rate_percent: e.target.value })
                }
                className="input num"
              />
            </Field>
            <Field label="תקופה (חודשים)">
              <input
                type="number"
                value={form.term_months}
                onChange={(e) => setForm({ ...form, term_months: e.target.value })}
                className="input num"
              />
            </Field>
            <Field label="חלויות שנתיות (₪)">
              <input
                type="number"
                value={form.current_annual_maturities}
                onChange={(e) =>
                  setForm({ ...form, current_annual_maturities: e.target.value })
                }
                className="input num"
              />
            </Field>
            <Field label="הערות" className="md:col-span-3">
              <textarea
                value={form.notes}
                onChange={(e) => setForm({ ...form, notes: e.target.value })}
                rows={2}
                className="input"
              />
            </Field>
          </div>

          {create.isError && (
            <ErrorBanner message={`שגיאה בהוספה: ${create.error.message}`} />
          )}

          <div className="flex justify-end gap-2 mt-6">
            <button
              type="button"
              onClick={() => {
                setForm(facEmpty);
                setAdding(false);
              }}
              disabled={create.isPending}
              className="btn-secondary disabled:opacity-50"
            >
              ביטול
            </button>
            <button
              type="button"
              onClick={() => create.mutate(form)}
              disabled={!form.company_id || create.isPending}
              className="btn-primary inline-flex items-center gap-2 disabled:opacity-50"
            >
              {create.isPending && <Loader2 className="w-4 h-4 animate-spin" />}
              שמור
            </button>
          </div>
        </div>
      )}

      <div className="card !p-0 overflow-hidden">
        {loading ? (
          <div className="p-8 text-center text-sm text-slate-500">טוען…</div>
        ) : error ? (
          <div className="p-8 text-center text-sm text-red-600">
            שגיאה בטעינה: {error.message}
          </div>
        ) : facilities.length === 0 ? (
          <div className="p-8 text-center text-sm text-slate-500">
            אין הלוואות מוזנות.
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 text-slate-600">
                <tr>
                  <Th>חברה</Th>
                  <Th>מכשיר</Th>
                  <Th>בנק</Th>
                  <Th>תווית</Th>
                  <Th align="left">יתרה (₪)</Th>
                  <Th align="left">ריבית</Th>
                  <Th align="left">חלויות (₪)</Th>
                  <Th />
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {facilities.map((f) => (
                  <tr key={f.id} className="hover:bg-slate-50">
                    <Td>{companyMap.get(f.company_id)?.short_label ?? '—'}</Td>
                    <Td>
                      <div>{labelOf(INSTRUMENT_TYPES, f.instrument_type)}</div>
                      {f.loan_type && (
                        <div className="text-xs text-slate-500">
                          {labelOf(LOAN_TYPES, f.loan_type)}
                        </div>
                      )}
                    </Td>
                    <Td>{f.bank_name ?? '—'}</Td>
                    <Td>{f.facility_label ?? '—'}</Td>
                    <Td align="left">{fmtMoney(f.amount_current)}</Td>
                    <Td align="left">{fmtRate(f.annual_rate)}</Td>
                    <Td align="left">{fmtMoney(f.current_annual_maturities)}</Td>
                    <Td align="left">
                      <button
                        type="button"
                        onClick={() => {
                          if (confirm('למחוק את ההלוואה?')) remove.mutate(f.id);
                        }}
                        disabled={remove.isPending}
                        className="text-slate-400 hover:text-red-600 transition disabled:opacity-50"
                        aria-label="מחק"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </Td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </section>
  );
}

// ---------------------------------------------------------------------------
// Collaterals
// ---------------------------------------------------------------------------

type CollForm = {
  company_id: string;
  collateral_type: string;
  description: string;
  status: string;
  value_current: string;
  haircut_percent: string;
  secures_facility_id: string;
  notes: string;
};

const collEmpty: CollForm = {
  company_id: '',
  collateral_type: 'real_estate',
  description: '',
  status: 'existing',
  value_current: '',
  haircut_percent: '',
  secures_facility_id: '',
  notes: '',
};

function CollateralsSection({
  caseId,
  companies,
  companyMap,
  collaterals,
  facilities,
  loading,
  error,
}: {
  caseId: string;
  companies: Company[];
  companyMap: Map<string, Company>;
  collaterals: Collateral[];
  facilities: Facility[];
  loading: boolean;
  error: Error | null;
}) {
  const qc = useQueryClient();
  const [adding, setAdding] = useState(false);
  const [form, setForm] = useState<CollForm>(collEmpty);

  const create = useMutation<Collateral, Error, CollForm>({
    mutationFn: (s) => {
      const body: Record<string, unknown> = {
        company_id: s.company_id,
        collateral_type: s.collateral_type,
        status: s.status,
      };
      if (s.description) body.description = s.description;
      if (s.value_current) body.value_current = s.value_current;
      if (s.haircut_percent) {
        body.haircut_ratio = (Number(s.haircut_percent) / 100).toString();
      }
      if (s.secures_facility_id) body.secures_facility_id = s.secures_facility_id;
      if (s.notes) body.notes = s.notes;
      return apiClient.post<Collateral>('/collaterals', body);
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['collaterals', 'case', caseId] });
      setForm(collEmpty);
      setAdding(false);
    },
  });

  const remove = useMutation<void, Error, string>({
    mutationFn: (id) => apiClient.delete(`/collaterals/${id}`),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['collaterals', 'case', caseId] });
    },
  });

  const startAdd = () => {
    const primary = companies.find((c) => c.is_primary) ?? companies[0];
    setForm({ ...collEmpty, company_id: primary?.id ?? '' });
    setAdding(true);
  };

  const facilitiesForCompany = facilities.filter((f) => f.company_id === form.company_id);

  return (
    <section>
      <div className="flex items-center justify-between mb-3">
        <h3 className="text-lg font-semibold text-navy">ביטחונות</h3>
        {!adding && (
          <button
            type="button"
            onClick={startAdd}
            className="btn-primary inline-flex items-center gap-2 text-sm"
          >
            <Plus className="w-4 h-4" />
            הוסף ביטחון
          </button>
        )}
      </div>

      {adding && (
        <div className="card mb-4">
          <h4 className="font-semibold text-navy mb-4">ביטחון חדש</h4>
          <div className="grid md:grid-cols-3 gap-4">
            <Field label="חברה">
              <select
                value={form.company_id}
                onChange={(e) => setForm({ ...form, company_id: e.target.value })}
                className="input"
              >
                <option value="">— בחר חברה —</option>
                {companies.map((c) => (
                  <option key={c.id} value={c.id}>
                    {c.short_label}
                  </option>
                ))}
              </select>
            </Field>
            <Field label="סוג ביטחון">
              <select
                value={form.collateral_type}
                onChange={(e) => setForm({ ...form, collateral_type: e.target.value })}
                className="input"
              >
                {COLLATERAL_TYPES.map((o) => (
                  <option key={o.value} value={o.value}>
                    {o.label}
                  </option>
                ))}
              </select>
            </Field>
            <Field label="סטטוס">
              <select
                value={form.status}
                onChange={(e) => setForm({ ...form, status: e.target.value })}
                className="input"
              >
                {COLLATERAL_STATUSES.map((o) => (
                  <option key={o.value} value={o.value}>
                    {o.label}
                  </option>
                ))}
              </select>
            </Field>
            <Field label="תיאור" className="md:col-span-2">
              <input
                type="text"
                value={form.description}
                onChange={(e) => setForm({ ...form, description: e.target.value })}
                placeholder="לדוגמה: דירה ברח׳ הנשיא 12 ירושלים"
                className="input"
              />
            </Field>
            <Field label="שווי קיים (₪)">
              <input
                type="number"
                value={form.value_current}
                onChange={(e) => setForm({ ...form, value_current: e.target.value })}
                className="input num"
              />
            </Field>
            <Field label="Haircut (%)">
              <input
                type="number"
                step="0.01"
                value={form.haircut_percent}
                onChange={(e) => setForm({ ...form, haircut_percent: e.target.value })}
                placeholder="לדוגמה: 70 (= 0.70)"
                className="input num"
              />
            </Field>
            <Field label="מבטיח הלוואה">
              <select
                value={form.secures_facility_id}
                onChange={(e) =>
                  setForm({ ...form, secures_facility_id: e.target.value })
                }
                disabled={!form.company_id || facilitiesForCompany.length === 0}
                className="input"
              >
                <option value="">— ללא קישור —</option>
                {facilitiesForCompany.map((f) => (
                  <option key={f.id} value={f.id}>
                    {f.facility_label || labelOf(INSTRUMENT_TYPES, f.instrument_type)}
                    {f.bank_name ? ` (${f.bank_name})` : ''}
                  </option>
                ))}
              </select>
            </Field>
            <Field label="הערות" className="md:col-span-3">
              <textarea
                value={form.notes}
                onChange={(e) => setForm({ ...form, notes: e.target.value })}
                rows={2}
                className="input"
              />
            </Field>
          </div>

          {create.isError && (
            <ErrorBanner message={`שגיאה בהוספה: ${create.error.message}`} />
          )}

          <div className="flex justify-end gap-2 mt-6">
            <button
              type="button"
              onClick={() => {
                setForm(collEmpty);
                setAdding(false);
              }}
              disabled={create.isPending}
              className="btn-secondary disabled:opacity-50"
            >
              ביטול
            </button>
            <button
              type="button"
              onClick={() => create.mutate(form)}
              disabled={!form.company_id || create.isPending}
              className="btn-primary inline-flex items-center gap-2 disabled:opacity-50"
            >
              {create.isPending && <Loader2 className="w-4 h-4 animate-spin" />}
              שמור
            </button>
          </div>
        </div>
      )}

      <div className="card !p-0 overflow-hidden">
        {loading ? (
          <div className="p-8 text-center text-sm text-slate-500">טוען…</div>
        ) : error ? (
          <div className="p-8 text-center text-sm text-red-600">
            שגיאה בטעינה: {error.message}
          </div>
        ) : collaterals.length === 0 ? (
          <div className="p-8 text-center text-sm text-slate-500">
            אין ביטחונות מוזנים.
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 text-slate-600">
                <tr>
                  <Th>חברה</Th>
                  <Th>סוג</Th>
                  <Th>תיאור</Th>
                  <Th>סטטוס</Th>
                  <Th align="left">שווי (₪)</Th>
                  <Th align="left">Haircut</Th>
                  <Th />
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {collaterals.map((c) => (
                  <tr key={c.id} className="hover:bg-slate-50">
                    <Td>{companyMap.get(c.company_id)?.short_label ?? '—'}</Td>
                    <Td>{labelOf(COLLATERAL_TYPES, c.collateral_type)}</Td>
                    <Td>{c.description ?? '—'}</Td>
                    <Td>{labelOf(COLLATERAL_STATUSES, c.status)}</Td>
                    <Td align="left">{fmtMoney(c.value_current)}</Td>
                    <Td align="left">{fmtRate(c.haircut_ratio)}</Td>
                    <Td align="left">
                      <button
                        type="button"
                        onClick={() => {
                          if (confirm('למחוק את הביטחון?')) remove.mutate(c.id);
                        }}
                        disabled={remove.isPending}
                        className="text-slate-400 hover:text-red-600 transition disabled:opacity-50"
                        aria-label="מחק"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </Td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </section>
  );
}

// ---------------------------------------------------------------------------
// Shared bits
// ---------------------------------------------------------------------------

function Field({
  label,
  className,
  children,
}: {
  label: string;
  className?: string;
  children: React.ReactNode;
}) {
  return (
    <div className={className}>
      <label className="block text-sm font-medium text-slate-700 mb-1">{label}</label>
      {children}
    </div>
  );
}

function Th({
  children,
  align = 'right',
}: {
  children?: React.ReactNode;
  align?: 'right' | 'left';
}) {
  return (
    <th
      className={`px-4 py-3 font-medium ${align === 'left' ? 'text-left' : 'text-right'}`}
    >
      {children}
    </th>
  );
}

function Td({
  children,
  align = 'right',
}: {
  children?: React.ReactNode;
  align?: 'right' | 'left';
}) {
  return (
    <td
      className={`px-4 py-3 text-slate-700 ${align === 'left' ? 'text-left' : 'text-right'}`}
    >
      {children}
    </td>
  );
}

function ErrorBanner({ message }: { message: string }) {
  return (
    <div className="flex items-start gap-2 p-3 mt-4 bg-red-50 border border-red-200 rounded-lg text-sm text-red-700">
      <AlertCircle className="w-4 h-4 mt-0.5 flex-shrink-0" />
      <span>{message}</span>
    </div>
  );
}
